import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';

import { FieldLabel } from '../../shared/ui/field-label';
import { Hint } from '../../shared/ui/hint';
import { Input } from '../../shared/ui/input';
import { Modal } from '../../shared/ui/modal';
import { MutationError } from '../../shared/ui/mutation-error';
import {
  formatBytes,
  QUOTA_GB as GB,
  setStorageQuota,
  useRefreshStorage,
} from '../../entities/retention';

interface IQuotaDialogProps {
  quotaBytes: number;
  totalBytes: number;
  onClose: () => void;
}

export const QuotaDialog = ({ quotaBytes, totalBytes, onClose }: IQuotaDialogProps) => {
  const refresh = useRefreshStorage();
  const [text, setText] = useState(String(Math.round(quotaBytes / GB)));
  const gigabytes = Number(text);
  const valid = Number.isInteger(gigabytes) && gigabytes >= 1 && gigabytes <= 100_000;
  const save = useMutation({
    mutationFn: () => setStorageQuota(gigabytes * GB),
    onSuccess: async () => {
      await refresh();
      onClose();
    },
  });

  return (
    <Modal
      open
      onOpenChange={(open) => !open && onClose()}
      title="Storage quota"
      description="For the whole organization. Over it, the oldest content goes first — whatever each flow keeps, Forever included."
      submit={{
        label: 'Save',
        onSubmit: () => save.mutate(),
        disabled: !valid || gigabytes * GB === quotaBytes,
        waiting: save.isPending,
        waitingLabel: 'Saving',
      }}
    >
      <div className="flex flex-col gap-2">
        <FieldLabel htmlFor="storage-quota">Quota</FieldLabel>
        <Input
          id="storage-quota"
          mono
          inputMode="numeric"
          value={text}
          invalid={!valid}
          trailing={<span className="text-meta text-faint">GB</span>}
          onChange={(event) => setText(event.target.value)}
        />
        <Hint>
          Stored now: {formatBytes(totalBytes)}.
          {valid && gigabytes * GB < totalBytes
            ? ' Below that, the oldest content is removed within minutes until it fits.'
            : ' Whole gigabytes, 1 to 100,000.'}
        </Hint>
        <MutationError error={save.error} />
      </div>
    </Modal>
  );
};
